import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { PAGE_SIZES } from '@app/shared';
import { EMPTY_TEXT, ErrorState } from './PageState';

export interface Column<T> {
  /** Unique key; also the sort field sent to the API when `sortable`. */
  key: string;
  header: string;
  render: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  nowrap?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyText?: string;
  /** 1-based page number. Omit all paging props for an unpaged table. */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  /** Current sort, `field` or `-field`. */
  sort?: string;
  onSortChange?: (sort: string) => void;
  /** Accessible name for the table. */
  label: string;
  /**
   * Child rows (the reference's expandable rows): a toggle before each row opens what `render`
   * returns under it; `label` names the row for the toggle's accessible name.
   */
  rowDetail?: { render: (row: T) => ReactNode; label: (row: T) => string };
}

/**
 * Server-driven table: the caller owns page, page size and sort and fetches accordingly.
 * Renders loading, error and empty states, and the "Total record" line the reference shows.
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const { rows, loading, error, sort, onSortChange, rowDetail } = props;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((now) => {
      const next = new Set(now);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const columns: Column<T>[] = rowDetail
    ? [
        {
          key: '__detail',
          header: '',
          width: 40,
          render: (row) => {
            const key = props.rowKey(row);
            const expanded = open.has(key);
            return (
              <IconButton
                size="small"
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Hide' : 'Show'} ${rowDetail.label(row)}`}
                onClick={() => toggle(key)}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            );
          },
        },
        ...props.columns,
      ]
    : props.columns;
  const paged =
    props.page !== undefined && props.pageSize !== undefined && props.total !== undefined;

  if (error) return <ErrorState error={error} onRetry={props.onRetry} />;

  const sortField = sort?.replace(/^-/, '');
  const sortDir: 'asc' | 'desc' = sort?.startsWith('-') ? 'desc' : 'asc';
  const toggleSort = (key: string) =>
    onSortChange?.(sortField === key && sortDir === 'asc' ? `-${key}` : key);

  return (
    <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
      {paged ? (
        <Typography sx={{ px: 1.5, py: 0.75, fontWeight: 600 }}>
          Total record: {props.total}
        </Typography>
      ) : null}
      <Box sx={{ height: 4 }}>{loading ? <LinearProgress aria-label="Loading" /> : null}</Box>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
        <Table stickyHeader aria-label={props.label}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} sx={{ width: c.width }}>
                  {c.sortable && onSortChange ? (
                    <TableSortLabel
                      active={sortField === c.key}
                      direction={sortField === c.key ? sortDir : 'asc'}
                      onClick={() => toggleSort(c.key)}
                    >
                      {c.header}
                    </TableSortLabel>
                  ) : (
                    c.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row, i) => {
              const key = props.rowKey(row);
              return (
                <Fragment key={key}>
                  <TableRow hover>
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        sx={c.nowrap ? { whiteSpace: 'nowrap' } : undefined}
                      >
                        {c.render(row, i)}
                      </TableCell>
                    ))}
                  </TableRow>
                  {rowDetail && open.has(key) ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ py: 0, bgcolor: 'action.hover' }}>
                        <Collapse in unmountOnExit>
                          <Box sx={{ py: 1 }}>{rowDetail.render(row)}</Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
            {rows && rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                  {props.emptyText ?? EMPTY_TEXT}
                </TableCell>
              </TableRow>
            ) : null}
            {!rows && loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {paged ? (
        <TablePagination
          component="div"
          count={props.total!}
          page={Math.max(0, props.page! - 1)}
          rowsPerPage={props.pageSize!}
          rowsPerPageOptions={[...(props.pageSizeOptions ?? PAGE_SIZES)]}
          labelRowsPerPage="Record limit:"
          onPageChange={(_e, p) => props.onPageChange?.(p + 1)}
          onRowsPerPageChange={(e) => props.onPageSizeChange?.(Number(e.target.value))}
          showFirstButton
          showLastButton
        />
      ) : null}
    </Paper>
  );
}

/** Row number for the "#" column of a paged table. */
export const rowNumber = (index: number, page = 1, pageSize = 0): number =>
  (page - 1) * pageSize + index + 1;
